import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { IUser } from '@interfaces/user.interface';
import { SnackbarNotificationService } from 'ngx-lib';
import { UserRoles } from '../models/enums/UserRoles';
import { AwaiterService } from './awaiter.service';
import { LoginService } from './http/clients/login.service';
import { PostHogService } from './posthog.service';
import { UserOrganizationStorageService } from './user-organization-storage.service';
import { UserStorageService } from './user-storage.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    readonly user!: Signal<IUser | undefined>;
    readonly isLoggedIn!: Signal<boolean>;
    readonly hasCaddieAccess!: Signal<boolean>;

    private readonly router: Router = inject(Router);
    private readonly awaiter: AwaiterService = inject(AwaiterService);
    private readonly snackbarNotificationService: SnackbarNotificationService = inject(SnackbarNotificationService);
    private readonly loginService: LoginService = inject(LoginService);
    private readonly userStorageService: UserStorageService = inject(UserStorageService);
    private readonly userOrganizationStorageService: UserOrganizationStorageService =
        inject(UserOrganizationStorageService);
    private readonly posthogService: PostHogService = inject(PostHogService);

    private _user!: WritableSignal<IUser | undefined>;
    private _hasCaddieAccess!: WritableSignal<boolean>;

    constructor() {
        this._user = signal(this.getUserFromStorage());
        this.user = this._user.asReadonly();
        this.isLoggedIn = computed(() => !!this._user());
        this._hasCaddieAccess = signal(this._user()?.hasCaddieAccess ?? false);
        this.hasCaddieAccess = this._hasCaddieAccess.asReadonly();
    }

    getUserFromStorage(): IUser | undefined {
        return this.userStorageService.getUser();
    }

    redirectIfAuthenticated(redirectTo = '/dashboard') {
        this.router.navigate([redirectTo]);
    }

    async login(credentials: { email: string; password: string; rememberUser: boolean }): Promise<void> {
        await this.awaiter.awaitAction('Logging in', async () => {
            const result = await this.loginService.authenticate(credentials);

            const loginSuccess = result.isAuthorized;

            if (loginSuccess) {
                let defaultOrganization;
                let userRoleInDefaultOrganization;

                if (result.userOrganizations?.length) {
                    defaultOrganization = result.userOrganizations[0];
                    userRoleInDefaultOrganization = defaultOrganization.userRoleId;

                    this.userOrganizationStorageService.setUserOrganization({
                        id: defaultOrganization.organizationId,
                        roleId: userRoleInDefaultOrganization,
                        code: defaultOrganization.organizationCode
                    });
                }

                const user: IUser = {
                    id: result.id!,
                    email: result.email!,
                    firstName: result.firstName!,
                    lastName: result.lastName!,
                    roleId: userRoleInDefaultOrganization,
                    hasCaddieAccess: result.hasCaddieAccess
                };

                this.userStorageService.setUser(user);
                const updatedUser = this.userStorageService.getUser();
                this._user.set(updatedUser);
                this._hasCaddieAccess.set(user.hasCaddieAccess ?? false);
                this.posthogService.posthog.capture('user_logged_in', {
                    has_default_organization: Boolean(defaultOrganization),
                    role_id: userRoleInDefaultOrganization
                });
                this.router.navigate(['/dashboard']);
            } else {
                this.snackbarNotificationService.error('Login failed - not authorized');
            }
        });
    }

    async logout() {
        await this.loginService.logout();
        this.posthogService.posthog.capture('user_logged_out', {});
        this.posthogService.posthog.reset();
        this.userStorageService.removeUser();
        this.userOrganizationStorageService.removeUserOrganization();
        this._hasCaddieAccess.set(false);
        this._user.set(undefined);
        this.router.navigate(['/login'], { replaceUrl: true });
    }

    hasRestrictedAccess(roleToCompare: UserRoles = UserRoles.SalesAdmin): boolean {
        const userOrganization = this.userOrganizationStorageService.getUserOrganization();
        return !userOrganization?.roleId || userOrganization?.roleId < roleToCompare;
    }

    async sendPasswordResetEmail(email: string): Promise<void> {
        if (!email) {
            this.snackbarNotificationService.error('Email is required');
            return;
        }

        await this.awaiter.awaitAction('Sending Forgot Password Request', async () => {
            await this.loginService.sendForgotPasswordRequest({
                email
            });
            this.snackbarNotificationService.success('Email successfully sent.');
        });
    }

    updateUserInfo(updatedUser: Partial<IUser>): void {
        const currentUser = this._user();
        if (currentUser) {
            const mergedUser = { ...currentUser, ...updatedUser };
            this._user.set(mergedUser);
            this.userStorageService.setUser(mergedUser);
        }
    }
}
